import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Loader2, ChevronLeft, KeyRound, Monitor, Clock, CheckCircle2, Smartphone } from "lucide-react";
import { api, humanizeAxiosError } from "../lib/api";
import { toast } from "sonner";

const RED = "#DC2626";
const fechaHora = (iso) => (iso ? new Date(iso).toLocaleString("es-PE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function parseUA(ua = "") {
  const os = /Windows/i.test(ua) ? "Windows" : /Mac/i.test(ua) ? "macOS" : /Android/i.test(ua) ? "Android" : /iPhone|iPad/i.test(ua) ? "iOS" : /Linux/i.test(ua) ? "Linux" : "Dispositivo";
  const br = /Edg/i.test(ua) ? "Edge" : /Chrome/i.test(ua) ? "Chrome" : /Firefox/i.test(ua) ? "Firefox" : /Safari/i.test(ua) ? "Safari" : "Navegador";
  return `${br} · ${os}`;
}

export default function SeguridadPage() {
  const [resumen, setResumen] = useState(undefined);
  const [actividad, setActividad] = useState([]);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirmar: "" });
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    try {
      const [r, a] = await Promise.all([api.get("/auth/seguridad/resumen"), api.get("/auth/actividad")]);
      setResumen(r.data); setActividad(a.data.items || []);
    } catch { setResumen(null); }
  };
  useEffect(() => { cargar(); }, []);

  const cambiar = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) { toast.error("La nueva contraseña debe tener al menos 8 caracteres."); return; }
    if (form.new_password !== form.confirmar) { toast.error("Las contraseñas no coinciden."); return; }
    setGuardando(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Contraseña actualizada correctamente.");
      setForm({ current_password: "", new_password: "", confirmar: "" });
      await cargar();
    } catch (err) { toast.error(humanizeAxiosError(err)); }
    finally { setGuardando(false); }
  };

  return (
    <div className="px-5 md:px-8 py-8 max-w-3xl" data-testid="seguridad-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"><ChevronLeft size={16} /> Volver al Ecosistema</Link>
      <h1 className="mt-4 text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight inline-flex items-center gap-3"><Shield size={30} style={{ color: RED }} /> Seguridad</h1>
      <p className="mt-2 text-slate-600">Contraseña, autenticación y actividad de acceso de tu cuenta.</p>

      {resumen && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Método de acceso</p>
            <p className="mt-1 font-bold text-slate-900">{resumen.metodo}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Accesos registrados</p>
            <p className="mt-1 font-bold text-slate-900">{resumen.accesos_registrados}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Último acceso</p>
            <p className="mt-1 font-bold text-slate-900 text-sm">{fechaHora(resumen.ultimo_acceso)}</p>
          </div>
        </div>
      )}

      {/* Cambiar contraseña */}
      {resumen?.tiene_password !== false && (
        <section className="mt-6 bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="seguridad-password-card">
          <h2 className="font-bold text-slate-900 inline-flex items-center gap-2"><KeyRound size={18} style={{ color: RED }} /> Cambiar contraseña</h2>
          <form onSubmit={cambiar} className="mt-4 space-y-3 max-w-md">
            <input type="password" value={form.current_password} onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))} data-testid="seg-actual" placeholder="Contraseña actual" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
            <input type="password" value={form.new_password} onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))} data-testid="seg-nueva" placeholder="Nueva contraseña (mín. 8 caracteres)" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
            <input type="password" value={form.confirmar} onChange={(e) => setForm((f) => ({ ...f, confirmar: e.target.value }))} data-testid="seg-confirmar" placeholder="Confirmar nueva contraseña" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
            <button type="submit" disabled={guardando} data-testid="seg-guardar" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold hover:brightness-110 disabled:opacity-60" style={{ backgroundColor: RED }}>
              {guardando ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Actualizar contraseña
            </button>
          </form>
        </section>
      )}

      {/* 2FA próximamente */}
      <section className="mt-4 bg-white rounded-2xl border border-slate-100 shadow-soft p-5 flex items-center justify-between gap-4" data-testid="seguridad-2fa-card">
        <div className="inline-flex items-center gap-3">
          <Smartphone size={20} className="text-slate-500" />
          <div><p className="font-bold text-slate-900 text-sm">Autenticación en dos pasos (2FA)</p><p className="text-xs text-slate-500">Añade una capa extra de seguridad a tu cuenta.</p></div>
        </div>
        <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-100 text-slate-500">Próximamente</span>
      </section>

      {/* Actividad de acceso */}
      <section className="mt-4 bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="seguridad-actividad-card">
        <h2 className="font-bold text-slate-900 inline-flex items-center gap-2"><Monitor size={18} style={{ color: RED }} /> Actividad de acceso reciente</h2>
        {resumen === undefined && <div className="mt-3 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Cargando…</div>}
        {resumen && actividad.length === 0 && <p className="mt-3 text-sm text-slate-400">No hay registros de acceso todavía.</p>}
        <div className="mt-3 divide-y divide-slate-100">
          {actividad.map((a, i) => (
            <div key={i} className="flex items-center gap-3 py-3" data-testid={`seg-actividad-${i}`}>
              <span className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center text-slate-500"><Monitor size={16} /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800">{parseUA(a.user_agent)}</p>
                <p className="text-xs text-slate-500 inline-flex items-center gap-1"><Clock size={11} /> {fechaHora(a.fecha)} · IP {a.ip || "—"}</p>
              </div>
              {i === 0 && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600"><CheckCircle2 size={11} /> Más reciente</span>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
